import type { PartialDeep } from 'type-fest';

import { FeatureBundleSet } from 'enums/featureBundle';

import type { FeatureBundle, FeatureSetInput, Rooftop } from 'store/client/graphql';
import { GuaranteedOfferPackage, PaymentOption } from 'store/client/graphql';

export interface FeatureEnabledForRooftopParams {
  feature: FeatureBundleSet;
  rooftop: PartialDeep<Record<'bundle', Pick<FeatureBundle, 'features'>>>;
}

export const isFeatureEnabledForRooftop = ({ feature, rooftop }: FeatureEnabledForRooftopParams): boolean => {
  switch (feature) {
    case FeatureBundleSet.CONVERSATION: {
      return !!rooftop?.bundle?.features?.lead?.conversations;
    }

    case FeatureBundleSet.LEAD_FORWARDING: {
      return !!rooftop?.bundle?.features?.lead?.forwarding;
    }

    case FeatureBundleSet.RETAIL_AI_DESCRIPTIONS: {
      return !!rooftop?.bundle?.features?.retail?.aiDescriptions;
    }

    case FeatureBundleSet.RETAIL_BUILD_AND_PRICE: {
      return !!rooftop?.bundle?.features?.retail?.buildAndPrice;
    }

    case FeatureBundleSet.RETAIL_CASH_PAYMENT_OPTION: {
      return !!rooftop?.bundle?.features?.retail?.paymentOptions?.includes(PaymentOption.CASH);
    }

    case FeatureBundleSet.RETAIL_FINANCE_PAYMENT_OPTION: {
      return !!rooftop?.bundle?.features?.retail?.paymentOptions?.includes(PaymentOption.FINANCE);
    }

    case FeatureBundleSet.RETAIL_LEASE_PAYMENT_OPTION: {
      return !!rooftop?.bundle?.features?.retail?.paymentOptions?.includes(PaymentOption.LEASE);
    }

    case FeatureBundleSet.RETAIL_PAYMENT_OPTION: {
      const isPaymentOptionEnabled =
        !!rooftop?.bundle?.features?.retail?.paymentOptions?.includes(PaymentOption.LEASE) ||
        !!rooftop?.bundle?.features?.retail?.paymentOptions?.includes(PaymentOption.CASH) ||
        !!rooftop?.bundle?.features?.retail?.paymentOptions?.includes(PaymentOption.FINANCE);

      return isPaymentOptionEnabled;
    }

    case FeatureBundleSet.RETAIL_SHOWROOM: {
      return !!rooftop?.bundle?.features?.retail?.showroom;
    }
  }

  return !!rooftop?.bundle?.features?.[feature]?.enabled;
};

/**
 * Check if the rooftop has `guaranteedOfferPackage` set and is not Legacy (GT)
 * @param rooftop The rooftop to check
 */
export const isGuaranteedOfferEnabledForRooftop = (rooftop: Partial<Rooftop>) => {
  const guaranteedOfferPackage = rooftop?.bundle?.features?.tradeIn?.guaranteedOfferPackage;

  return !!guaranteedOfferPackage && guaranteedOfferPackage !== GuaranteedOfferPackage.LEGACY;
};

export const isEnabledForSomeRooftops = (
  feature: FeatureBundleSet,
  rooftops: Record<'bundle', PartialDeep<FeatureBundle>>[]
): boolean => rooftops?.some(rooftop => isFeatureEnabledForRooftop({ feature, rooftop }));

/**
 * Check to see if a feature is enabled for every one of the provided rooftops. Returns false for an
 * empty list, so a feature is never reported enabled when no rooftop backs it.
 * @example isEnabledForAllRooftops(FeatureBundleSet.RETAIL_SHOWROOM, rooftops)
 */
export const isEnabledForAllRooftops = (
  feature: FeatureBundleSet,
  rooftops: Record<'bundle', PartialDeep<FeatureBundle>>[]
): boolean => !!rooftops?.length && rooftops.every(rooftop => isFeatureEnabledForRooftop({ feature, rooftop }));

export const doUserRooftopsHaveFeatureEnabled = (
  feature: FeatureBundleSet,
  userRooftops?: null | Record<'bundle', PartialDeep<FeatureBundle>>[]
): boolean => {
  // If the user has no access to any rooftoops, then they are a whitelabel scoped and can still access all routes
  if (!userRooftops?.length) {
    return true;
  }

  return isEnabledForSomeRooftops(feature, userRooftops);
};

export const featureBundleSetToFeatureSetInput = (feature: FeatureBundleSet): FeatureSetInput => {
  switch (feature) {
    case FeatureBundleSet.APPOINTMENT: {
      return { appointment: { enabled: true } };
    }

    case FeatureBundleSet.CONVERSATION: {
      return { lead: { conversations: true } };
    }

    case FeatureBundleSet.LEAD: {
      return { lead: { enabled: true } };
    }

    case FeatureBundleSet.LEAD_FORWARDING: {
      return { lead: { forwarding: true } };
    }

    case FeatureBundleSet.RETAIL: {
      return { retail: { enabled: true } };
    }

    case FeatureBundleSet.RETAIL_AI_DESCRIPTIONS: {
      return { retail: { aiDescriptions: true } };
    }

    case FeatureBundleSet.RETAIL_BUILD_AND_PRICE: {
      return { retail: { buildAndPrice: true } };
    }

    case FeatureBundleSet.RETAIL_CASH_PAYMENT_OPTION: {
      return { retail: { paymentOptions: [PaymentOption.CASH] } };
    }

    case FeatureBundleSet.RETAIL_FINANCE_PAYMENT_OPTION: {
      return { retail: { paymentOptions: [PaymentOption.FINANCE] } };
    }

    case FeatureBundleSet.RETAIL_LEASE_PAYMENT_OPTION: {
      return { retail: { paymentOptions: [PaymentOption.LEASE] } };
    }

    case FeatureBundleSet.RETAIL_PAYMENT_OPTION: {
      return { retail: { paymentOptions: [PaymentOption.CASH, PaymentOption.FINANCE, PaymentOption.LEASE] } };
    }

    case FeatureBundleSet.RETAIL_SHOWROOM: {
      return { retail: { showroom: true } };
    }

    case FeatureBundleSet.TASK: {
      return { task: { enabled: true } };
    }

    case FeatureBundleSet.TRADE_IN: {
      return { tradeIn: { enabled: true } };
    }

    default: {
      return {};
    }
  }
};
